// Evidence-level + relationship-type visual systems (CODEX design guidelines).
// 9-level evidence taxonomy + relationship types. Colors applied via inline styles
// to stay JIT-safe. Legacy backend keys are aliased to the new taxonomy.
//
// Palette (approved brand system):
//   Evidence Green #73D68A | Scholarly Blue #64B5F6 | Limited Amber #E3B341 |
//   Speculative Orange #D88932 | Unsupported Red #D45B5B | Mystery Violet #8E6AD8 |
//   Antique Gold #D8B66A | Muted Brass #B68A3A | Champagne #F4DE9F | Slate #7E8492 |
//   Main Text #F4E8C8

export const EVIDENCE = {
  // ---- Official 9-level taxonomy ----
  well_documented: { key: "well_documented", label: "Well Documented", hex: "#73D68A" },
  historically_supported: { key: "historically_supported", label: "Historically Supported", hex: "#D8B66A" },
  academic_scientific: { key: "academic_scientific", label: "Academic / Scientific", hex: "#64B5F6" },
  declassified_primary_source: { key: "declassified_primary_source", label: "Declassified / Primary Source", hex: "#64B5F6" },
  eyewitness_based: { key: "eyewitness_based", label: "Eyewitness-Based", hex: "#7E8492" },
  limited_evidence: { key: "limited_evidence", label: "Limited Evidence", hex: "#E3B341" },
  competing_interpretations: { key: "competing_interpretations", label: "Competing Interpretations", hex: "#8E6AD8" },
  speculative: { key: "speculative", label: "Speculative", hex: "#D88932" },
  unsupported_urban_legend: { key: "unsupported_urban_legend", label: "Unsupported / Urban Legend", hex: "#D45B5B" },
};

// ---- Legacy backend keys → new taxonomy entries ----
const EVIDENCE_ALIASES = {
  documented: EVIDENCE.well_documented,
  strong_scholarly: EVIDENCE.academic_scientific,
  scholarly: EVIDENCE.academic_scientific,
  historical: EVIDENCE.historically_supported,
  declassified: EVIDENCE.declassified_primary_source,
  primary_source: EVIDENCE.declassified_primary_source,
  eyewitness: EVIDENCE.eyewitness_based,
  limited: EVIDENCE.limited_evidence,
  competing: EVIDENCE.competing_interpretations,
  urban_legend: EVIDENCE.unsupported_urban_legend,
  unsupported: EVIDENCE.unsupported_urban_legend,
};

// Ordered list (strongest → weakest) for legends and filters.
export const EVIDENCE_ORDER = [
  "well_documented",
  "historically_supported",
  "academic_scientific",
  "declassified_primary_source",
  "eyewitness_based",
  "limited_evidence",
  "competing_interpretations",
  "speculative",
  "unsupported_urban_legend",
];

// Filter options aligned to the values the backend actually stores on topics.
// `value` is sent to the API; label/hex are the branded display via the taxonomy.
export const EVIDENCE_FILTERS = [
  { value: "documented", label: EVIDENCE.well_documented.label, hex: EVIDENCE.well_documented.hex },
  { value: "strong_scholarly", label: EVIDENCE.academic_scientific.label, hex: EVIDENCE.academic_scientific.hex },
  { value: "limited", label: EVIDENCE.limited_evidence.label, hex: EVIDENCE.limited_evidence.hex },
  { value: "speculative", label: EVIDENCE.speculative.label, hex: EVIDENCE.speculative.hex },
  { value: "urban_legend", label: EVIDENCE.unsupported_urban_legend.label, hex: EVIDENCE.unsupported_urban_legend.hex },
];

export const RELATIONSHIP = {
  biblical: { key: "biblical", label: "Biblical", hex: "#D8B66A" },
  historical: { key: "historical", label: "Historical", hex: "#B68A3A" },
  scientific: { key: "scientific", label: "Scientific", hex: "#64B5F6" },
  government: { key: "government", label: "Government", hex: "#7E8492" },
  theological: { key: "theological", label: "Theological", hex: "#E3B341" },
  philosophical: { key: "philosophical", label: "Philosophical", hex: "#8E6AD8" },
  cultural: { key: "cultural", label: "Cultural", hex: "#73D68A" },
  mythological: { key: "mythological", label: "Mythological", hex: "#D88932" },
  literary: { key: "literary", label: "Literary", hex: "#F4E8C8" },
  media: { key: "media", label: "Media", hex: "#64B5F6" },
  speculative: { key: "speculative", label: "Speculative", hex: "#D45B5B" },
  // legacy
  archaeological: { key: "archaeological", label: "Archaeological", hex: "#B68A3A" },
};

// Relationship strengths (design guidelines).
export const RELATIONSHIP_STRENGTH = {
  strong_documented: { key: "strong_documented", label: "Strong Documented Connection", hex: "#73D68A" },
  scholarly: { key: "scholarly", label: "Scholarly Connection", hex: "#64B5F6" },
  thematic: { key: "thematic", label: "Thematic Connection", hex: "#D8B66A" },
  popular_theory: { key: "popular_theory", label: "Popular Theory Connection", hex: "#E3B341" },
  speculative: { key: "speculative", label: "Speculative Connection", hex: "#D45B5B" },
};

export const CONNECTION_TYPE = {
  clear_teaching: { label: "Clear biblical teaching", hex: "#73D68A" },
  thematic_parallel: { label: "Thematic parallel", hex: "#D8B66A" },
  speculative: { label: "Speculative connection", hex: "#D88932" },
};

export function hexToRgba(hex, alpha = 1) {
  const h = (hex || "#D8B66A").replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export function evidenceOf(key) {
  if (!key) return EVIDENCE.limited_evidence;
  const k = String(key).toLowerCase().replace(/[\s/-]+/g, "_");
  return EVIDENCE[k] || EVIDENCE_ALIASES[k] || EVIDENCE.limited_evidence;
}

export function relationshipOf(key) {
  if (!key) return RELATIONSHIP.cultural;
  const k = String(key).toLowerCase().replace(/[\s/-]+/g, "_");
  return RELATIONSHIP[k] || RELATIONSHIP.cultural;
}
